// packages/brand-config/src/applyBrandTheme.ts
import type { BrandConfig, BrandTokens } from "./types";

const TOKEN_TO_CSS_VAR: Partial<Record<keyof Required<BrandTokens>, string>> = {
  primaryColor: "--custom-primary-color-light",
  primaryColorHover: "--custom-primary-color-light-hover",
  primaryColorDark: "--custom-primary-color-dark",
  primaryColorDarkHover: "--custom-primary-color-dark-hover",
  textPrimary: "--custom-text-primary-light",
  textSecondary: "--custom-text-secondary-light",
  textTertiary: "--custom-text-tertiary-light",
  textDisabled: "--custom-text-disabled-light",
  textQuaternary: "--custom-text-quaternary-light",
  textSubtle: "--custom-text-subtle-light",
  textPrimaryDark: "--custom-text-primary-dark",
  textSecondaryDark: "--custom-text-secondary-dark",
  textTertiaryDark: "--custom-text-tertiary-dark",
  textDisabledDark: "--custom-text-disabled-dark",
  textQuaternaryDark: "--custom-text-quaternary-dark",
  textSubtleDark: "--custom-text-subtle-dark",
  successColor: "--success-color",
  warningColor: "--warning-color",
  errorColor: "--error-color",
  infoColor: "--info-color",
  deleteColor: "--error-color",
};

const LEGACY_THEME_COLOR_VARS = [
  "--primary-color",
  "--primary-color-hover",
  "--text-primary",
  "--text-secondary",
  "--text-tertiary",
  "--text-disabled",
  "--text-quaternary",
  "--text-subtle",
];

/**
 * 将品牌 token 写入 CSS 变量（默认 documentElement，子应用可传容器根节点）
 */
export const applyBrandTokens = (
  tokens: BrandTokens,
  element?: HTMLElement
): void => {
  const target = element ?? document.documentElement;

  LEGACY_THEME_COLOR_VARS.forEach((cssVar) => {
    target.style.removeProperty(cssVar);
  });

  (Object.keys(TOKEN_TO_CSS_VAR) as (keyof BrandTokens)[]).forEach((key) => {
    const value = tokens[key];
    const cssVar = TOKEN_TO_CSS_VAR[key as keyof Required<BrandTokens>];
    if (value && cssVar) {
      target.style.setProperty(cssVar, value);
    }
  });
};

export const applyBrandAssets = (
  assets: NonNullable<BrandConfig["assets"]>,
  element?: HTMLElement,
  theme?: "light" | "dark"
): void => {
  const target = element ?? document.documentElement;
  const resolvedTheme =
    theme ?? (target.getAttribute("data-theme") === "dark" ? "dark" : "light");

  if (assets.logoShrink) {
    target.style.setProperty(
      "--logo-img-shrink",
      `url("${assets.logoShrink}")`
    );
  }

  const logo =
    resolvedTheme === "dark"
      ? (assets.logoDark ?? assets.logoLight)
      : (assets.logoLight ?? assets.logoDark);

  if (logo) {
    target.style.setProperty("--logo-img", `url("${logo}")`);
    // 品牌未提供专用 Mac 顶栏图时，与 Windows 共用品牌 logo（如 EXX），
    // 避免落到 shared 的前行者 Mac 资源；并加宽 Mac 顶栏展示区。
    target.style.setProperty("--logo-img-mac", `url("${logo}")`);
    target.style.setProperty("--logo-img-mac-width", "160px");
    target.style.setProperty("--logo-img-mac-height", "28px");
  }
};

/**
 * 应用完整品牌配置（颜色 + 可选 Logo 变量）
 */
export const applyBrandTheme = (
  config: BrandConfig,
  element?: HTMLElement
): void => {
  const target = element ?? document.documentElement;
  const brandId = config.meta?.id?.trim().toLowerCase();
  if (brandId) {
    target.setAttribute("data-brand", brandId);
  }
  applyBrandTokens(config.tokens, element);
  if (config.assets) {
    applyBrandAssets(config.assets, element);
  }
};
